import React, { useState } from 'react';
import { motion } from 'motion/react';
import { ForkKnife, Barbell, MapPin, ListChecks, ListNumbers, CheckCircle, Circle } from '@phosphor-icons/react';

const TYPE_CONFIG = {
  recipe: {
    label: 'RECIPE',
    icon: <ForkKnife size={16} />,
  },
  workout: {
    label: 'WORKOUT',
    icon: <Barbell size={16} />,
  },
  itinerary: {
    label: 'ITINERARY',
    icon: <MapPin size={16} />,
  },
  checklist: {
    label: 'CHECKLIST',
    icon: <ListChecks size={16} />,
  },
  steps: {
    label: 'STEPS',
    icon: <ListNumbers size={16} />,
  },
};

const INGREDIENT_COLLAPSE_THRESHOLD = 8;
const STEP_COLLAPSE_THRESHOLD = 6;

export default function StructuredData({ structuredData }) {
  if (!structuredData || !structuredData.type) return null;

  const config = TYPE_CONFIG[structuredData.type];
  const label = config?.label || structuredData.type?.toUpperCase() || 'DATA';
  const icon = config?.icon || null;

  const renderers = {
    recipe: RecipeCard,
    workout: WorkoutCard,
    itinerary: ItineraryCard,
    checklist: ChecklistCard,
    steps: StepsCard,
  };

  const Renderer = renderers[structuredData.type] || GenericCard;

  return (
    <motion.div
      className="structured-data-card"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      <div className="structured-data-card-header">
        <span className="structured-data-type-badge">
          {icon}
          {label}
        </span>
      </div>
      <Renderer data={structuredData.data || structuredData} />
    </motion.div>
  );
}

function RecipeCard({ data }) {
  const [showAllIngredients, setShowAllIngredients] = useState(false);
  const [showAllSteps, setShowAllSteps] = useState(false);

  const ingredients = data.ingredients || [];
  const steps = data.steps || [];
  const hasHiddenIngredients = ingredients.length > INGREDIENT_COLLAPSE_THRESHOLD;
  const hasHiddenSteps = steps.length > STEP_COLLAPSE_THRESHOLD;

  const visibleIngredients = showAllIngredients
    ? ingredients
    : ingredients.slice(0, INGREDIENT_COLLAPSE_THRESHOLD);
  const visibleSteps = showAllSteps
    ? steps
    : steps.slice(0, STEP_COLLAPSE_THRESHOLD);

  const metaPills = [];
  if (data.servings) metaPills.push(data.servings + (typeof data.servings === 'number' ? ' srv' : ''));
  if (data.prep_time) metaPills.push(data.prep_time + ' prep');
  if (data.cook_time) metaPills.push(data.cook_time + ' cook');

  return (
    <div className="structured-data-card-body">
      {data.title && <div className="structured-data-title">{data.title}</div>}

      {metaPills.length > 0 && (
        <div className="structured-data-meta-pills">
          {metaPills.map((pill, i) => (
            <span key={i} className="structured-data-meta-pill">{pill}</span>
          ))}
        </div>
      )}

      {ingredients.length > 0 && (
        <div className="structured-data-section">
          <div className="structured-data-section-label">Ingredients</div>
          <div className="structured-data-ingredients">
            {visibleIngredients.map((ing, i) => (
              <div key={i} className="structured-data-ingredient">
                <span className="structured-data-ingredient-amount">
                  {ing.amount || ''}
                </span>
                <span className="structured-data-ingredient-item">
                  {ing.item ? (
                    <a
                      href={`https://google.com/search?q=${encodeURIComponent(ing.item)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {ing.item}
                    </a>
                  ) : null}
                  {ing.notes && (
                    <span className="structured-data-ingredient-notes">
                      {' '}&middot; {ing.notes}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
          {hasHiddenIngredients && !showAllIngredients && (
            <button
              className="structured-data-expand-btn"
              onClick={() => setShowAllIngredients(true)}
            >
              + Show {ingredients.length - INGREDIENT_COLLAPSE_THRESHOLD} more ingredient{ingredients.length - INGREDIENT_COLLAPSE_THRESHOLD > 1 ? 's' : ''}
            </button>
          )}
        </div>
      )}

      {steps.length > 0 && (
        <div className="structured-data-section">
          <div className="structured-data-section-label">Steps</div>
          <ol className="structured-data-steps">
            {visibleSteps.map((step, i) => (
              <li key={i} className="structured-data-step">{step}</li>
            ))}
          </ol>
          {hasHiddenSteps && !showAllSteps && (
            <button
              className="structured-data-expand-btn"
              onClick={() => setShowAllSteps(true)}
            >
              + Show {steps.length - STEP_COLLAPSE_THRESHOLD} more step{steps.length - STEP_COLLAPSE_THRESHOLD > 1 ? 's' : ''}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function WorkoutCard({ data }) {
  const equipment = data.equipment || [];
  const exercises = data.exercises || [];

  return (
    <div className="structured-data-card-body">
      {data.title && <div className="structured-data-title">{data.title}</div>}

      {data.duration && (
        <div className="structured-data-meta-pills">
          <span className="structured-data-meta-pill">{data.duration}</span>
        </div>
      )}

      {equipment.length > 0 && (
        <div className="structured-data-section">
          <div className="structured-data-section-label">Equipment</div>
          <div className="structured-data-chips">
            {equipment.map((item, i) => (
              <span key={i} className="structured-data-chip">{item}</span>
            ))}
          </div>
        </div>
      )}

      {exercises.length > 0 && (
        <div className="structured-data-section">
          <div className="structured-data-section-label">Exercises</div>
          <div className="structured-data-exercise-table">
            <div className="structured-data-exercise-header">
              <span className="structured-data-exercise-name-col">Exercise</span>
              <span className="structured-data-exercise-sets-col">Sets</span>
              <span className="structured-data-exercise-reps-col">Reps</span>
            </div>
            {exercises.map((ex, i) => (
              <div key={i} className={`structured-data-exercise-row ${i % 2 === 1 ? 'alt' : ''}`}>
                <span className="structured-data-exercise-name-col">
                  {ex.name ? (
                    <a
                      href={`https://google.com/search?q=${encodeURIComponent(ex.name + ' exercise form')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {ex.name}
                    </a>
                  ) : null}
                </span>
                <span className="structured-data-exercise-sets-col">{ex.sets || ''}</span>
                <span className="structured-data-exercise-reps-col">{ex.reps || ''}</span>
                {ex.notes && (
                  <div className="structured-data-exercise-notes">{ex.notes}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ItineraryCard({ data }) {
  const stops = data.stops || [];

  const dayGroups = [];
  let currentDay = null;
  let currentStops = [];
  stops.forEach((stop) => {
    const day = stop.day || null;
    if (day !== currentDay) {
      if (currentStops.length > 0) dayGroups.push({ day: currentDay, stops: currentStops });
      currentDay = day;
      currentStops = [stop];
    } else {
      currentStops.push(stop);
    }
  });
  if (currentStops.length > 0) dayGroups.push({ day: currentDay, stops: currentStops });

  const isMultiDay = dayGroups.length > 1;

  const renderTimeline = (timelineStops, startIndex = 0) => (
    <div className="structured-data-timeline">
      {timelineStops.map((stop, i) => (
        <div key={i} className="structured-data-timeline-stop">
          <div className="structured-data-timeline-marker">
            <span className="structured-data-timeline-day">{startIndex + i + 1}</span>
            {i < timelineStops.length - 1 && <div className="structured-data-timeline-line" />}
          </div>
          <div className="structured-data-timeline-content">
            {stop.location ? (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(stop.location)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="structured-data-timeline-location"
              >
                {stop.location}
              </a>
            ) : null}
            {stop.activity && (
              <div className="structured-data-timeline-activity">{stop.activity}</div>
            )}
            {stop.notes && (
              <div className="structured-data-timeline-notes">&middot; {stop.notes}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="structured-data-card-body">
      {data.title && <div className="structured-data-title">{data.title}</div>}

      {data.duration && (
        <div className="structured-data-meta-pills">
          <span className="structured-data-meta-pill">{data.duration}</span>
        </div>
      )}

      {stops.length > 0 && (
        <div className="structured-data-section">
          {isMultiDay
            ? dayGroups.map((group, gi) => (
                <div key={gi} className="structured-data-timeline-day-group">
                  {group.day != null && (
                    <div className="structured-data-timeline-day-header">Day {group.day}</div>
                  )}
                  {renderTimeline(group.stops)}
                </div>
              ))
            : renderTimeline(stops)}
        </div>
      )}
    </div>
  );
}

function ChecklistCard({ data }) {
  const items = data.items || [];

  return (
    <div className="structured-data-card-body">
      {data.title && <div className="structured-data-title">{data.title}</div>}

      {items.length > 0 && (
        <div className="structured-data-section">
          <div className="structured-data-checklist">
            {items.map((item, i) => (
              <div key={i} className={`structured-data-checklist-item ${item.checked ? 'checked' : ''}`}>
                <span className="structured-data-checkbox">
                  {item.checked ? (
                    <CheckCircle size={18} />
                  ) : (
                    <Circle size={18} />
                  )}
                </span>
                <div className="structured-data-checklist-text">
                  <span className="structured-data-checklist-label">{item.item}</span>
                  {item.notes && (
                    <div className="structured-data-checklist-notes">&middot; {item.notes}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StepsCard({ data }) {
  const [showAll, setShowAll] = useState(false);

  const steps = data.steps || [];
  const hasHidden = steps.length > STEP_COLLAPSE_THRESHOLD;
  const visibleSteps = showAll ? steps : steps.slice(0, STEP_COLLAPSE_THRESHOLD);

  return (
    <div className="structured-data-card-body">
      {data.title && <div className="structured-data-title">{data.title}</div>}

      {steps.length > 0 && (
        <div className="structured-data-section">
          <ol className="structured-data-steps">
            {visibleSteps.map((step, i) => {
              const stepText = typeof step === 'string' ? step : step.step;
              const stepNotes = typeof step === 'string' ? null : step.notes;
              return (
                <li key={i} className="structured-data-step">
                  {stepText}
                  {stepNotes && (
                    <div className="structured-data-step-notes">&middot; {stepNotes}</div>
                  )}
                </li>
              );
            })}
          </ol>
          {hasHidden && !showAll && (
            <button
              className="structured-data-expand-btn"
              onClick={() => setShowAll(true)}
            >
              + Show {steps.length - STEP_COLLAPSE_THRESHOLD} more step{steps.length - STEP_COLLAPSE_THRESHOLD > 1 ? 's' : ''}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function GenericCard({ data }) {
  return (
    <div className="structured-data-card-body">
      {data.title && (
        <div className="structured-data-title">{data.title}</div>
      )}
    </div>
  );
}
